import { useCallback } from "react";
import { AuthError, ProviderNotConfiguredError } from "../adapters/ports";
import { useLanguage } from "../i18n/LanguageProvider";
import { ApiError, NetworkError } from "../services/api-client";

/**
 * Turns any thrown value into a sentence in the current language.
 *
 * Errors cross the wire as stable codes rather than prose, so the message a
 * user reads comes from the catalogue. That is what makes failures fully
 * bilingual instead of falling back to an English string from the server.
 */
export const useErrorMessage = (): ((error: unknown) => string) => {
  const { t, messages } = useLanguage();

  return useCallback(
    (error: unknown): string => {
      if (error instanceof NetworkError) return t("errors.network");

      if (error instanceof ProviderNotConfiguredError) {
        return t("errors.adapter_not_configured");
      }

      if (error instanceof AuthError) {
        const key = `errors.auth.${error.code}`;
        const translated = t(key);
        return translated === key ? t("errors.auth.unknown") : translated;
      }

      if (error instanceof ApiError) {
        // Validation failures are shown next to the offending fields; this
        // is the summary line above the form.
        if (error.code === "validation_failed")
          return t("errors.validation_failed");

        const key = `errors.${error.code}`;
        const known = Object.prototype.hasOwnProperty.call(
          messages.errors,
          error.code,
        );
        if (known) return t(key);

        return error.status >= 500
          ? t("errors.internal_error")
          : t("errors.generic");
      }

      if (error instanceof DOMException && error.name === "AbortError") {
        return t("errors.generic");
      }

      return t("errors.generic");
    },
    [messages.errors, t],
  );
};

/**
 * Maps server-side field errors onto translated strings for a form.
 *
 * zod issue messages are developer-facing by default; where a schema uses a
 * deliberate code (such as `consent_to_contact_required`) that code is
 * translated, and anything else falls back to a generic message rather
 * than showing raw validator output to a user.
 */
export const useFieldErrors = (): ((
  error: unknown,
) => Record<string, string>) => {
  const { t, messages } = useLanguage();

  return useCallback(
    (error: unknown): Record<string, string> => {
      if (!(error instanceof ApiError) || error.code !== "validation_failed")
        return {};

      const output: Record<string, string> = {};
      for (const [field, issues] of Object.entries(error.fieldErrors)) {
        const first = issues[0];
        if (!first) continue;

        const known = Object.prototype.hasOwnProperty.call(
          messages.errors,
          first,
        );
        output[normalizeFieldPath(field)] = known
          ? t(`errors.${first}`)
          : t("errors.generic");
      }
      return output;
    },
    [messages.errors, t],
  );
};

/**
 * The API nests the application payload under `data`, but the form's own
 * state is flat, so `data.fullName` has to become `fullName` for the error
 * to land on the right input.
 */
const normalizeFieldPath = (field: string): string =>
  field.startsWith("data.") ? field.slice("data.".length) : field;
